import { useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Copy,
  History,
  Loader2,
  ShieldAlert,
  XCircle,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-master/components/ui/card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Input } from '@easyflow/ui-master/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import api from '@/lib/api';
import type { RestorePlanResponse, RestoreTargetKind } from '@easyflow/shared';

const TARGET_LABEL: Record<RestoreTargetKind, string> = {
  latest: 'Ultimo stato (latest)',
  time: 'Point-in-time (timestamp)',
  backup_label: 'Backup specifico (label)',
};

function CommandBlock({ command, onCopy }: { command: string; onCopy: () => void }) {
  return (
    <div className="rounded-md border bg-muted/40 p-3">
      <div className="flex items-start justify-between gap-2">
        <code className="font-mono text-xs break-all">{command}</code>
        <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={onCopy}>
          <Copy className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

export function RestorePanel() {
  const { toast } = useToast();
  const [targetKind, setTargetKind] = useState<RestoreTargetKind>('latest');
  const [value, setValue] = useState('');
  const [plan, setPlan] = useState<RestorePlanResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const needsValue = targetKind !== 'latest';

  const buildPlan = async () => {
    if (needsValue && !value.trim()) {
      toast({ title: 'Valore richiesto', description: 'Inserisci timestamp o label', variant: 'destructive' });
      return;
    }
    setLoading(true);
    setPlan(null);
    try {
      const params = new URLSearchParams({ target_kind: targetKind });
      if (needsValue) params.set('value', value.trim());
      const res = await api.get<RestorePlanResponse>(`/master/database/restore-plan?${params.toString()}`);
      if (res.data) setPlan(res.data);
      else toast({ title: 'Errore', description: 'Risposta vuota dal server', variant: 'destructive' });
    } catch (err) {
      const e = err as { response?: { data?: { error?: { message?: string } } }; message?: string };
      toast({
        title: 'Errore piano restore',
        description: e.response?.data?.error?.message ?? e.message ?? 'Errore di rete',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: 'Copiato', description: 'Comando copiato negli appunti' });
    } catch {
      toast({ title: 'Errore', description: 'Impossibile copiare', variant: 'destructive' });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <History className="h-4 w-4 text-purple-500" />
          Restore / PITR assistito
        </CardTitle>
        <CardDescription>
          Genera il piano di ripristino (comandi + passi Patroni). L'esecuzione è manuale sul nodo: nessuna operazione distruttiva parte dall'interfaccia.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {(['latest', 'time', 'backup_label'] as RestoreTargetKind[]).map((k) => (
            <Button
              key={k}
              variant={targetKind === k ? 'default' : 'outline'}
              className="justify-start"
              onClick={() => { setTargetKind(k); setPlan(null); }}
            >
              {targetKind === k ? <CheckCircle2 className="h-4 w-4 mr-2" /> : <Clock className="h-4 w-4 mr-2" />}
              {TARGET_LABEL[k]}
            </Button>
          ))}
        </div>

        {needsValue && (
          <Input
            placeholder={targetKind === 'time' ? 'es. 2026-06-21 14:30:00+02' : 'es. 20260621-140000F'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        )}

        <Button onClick={() => void buildPlan()} disabled={loading}>
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Genera piano
        </Button>

        {plan && (
          <div className="space-y-4 pt-2">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge variant="outline">target: {TARGET_LABEL[plan.target_kind]}</Badge>
              <Badge variant="outline" className="font-mono">{plan.resolved_target}</Badge>
              {plan.base_backup_label && (
                <Badge variant="outline" className="font-mono">base: {plan.base_backup_label}</Badge>
              )}
              {plan.wal_chain_complete ? (
                <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
                  <CheckCircle2 className="h-3 w-3 mr-1" /> catena WAL completa
                </Badge>
              ) : (
                <Badge className="bg-rose-500/15 text-rose-700 dark:text-rose-300">
                  <XCircle className="h-3 w-3 mr-1" /> catena WAL incompleta
                </Badge>
              )}
            </div>

            {plan.wal_chain_note && (
              <p className="text-xs text-muted-foreground">{plan.wal_chain_note}</p>
            )}

            {plan.warnings.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-500/5 p-3 space-y-1">
                {plan.warnings.map((w, i) => (
                  <p key={i} className="text-xs text-amber-700 dark:text-amber-300 flex items-start gap-1">
                    <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /> {w}
                  </p>
                ))}
              </div>
            )}

            <div>
              <h4 className="text-xs uppercase font-semibold text-muted-foreground mb-1">Comando restore principale</h4>
              {plan.restore_command ? (
                <CommandBlock command={plan.restore_command} onCopy={() => void copy(plan.restore_command ?? '')} />
              ) : (
                <div className="rounded-md border border-rose-300 bg-rose-500/5 p-3 text-xs text-rose-700 dark:text-rose-300 flex items-start gap-1">
                  <XCircle className="h-3 w-3 mt-0.5 shrink-0" />
                  Nessun comando disponibile: target non valido o catena WAL incompleta. Correggere il target prima di procedere.
                </div>
              )}
            </div>

            <div className="space-y-3">
              <h4 className="text-xs uppercase font-semibold text-muted-foreground">Passi operativi</h4>
              {plan.steps.map((s) => (
                <div key={s.order} className="space-y-1">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-purple-500/15 text-purple-700 dark:text-purple-300 text-xs">
                      {s.order}
                    </span>
                    {s.title}
                    <Badge variant="outline" className="font-mono text-xs">{s.node}</Badge>
                  </div>
                  <CommandBlock command={s.command} onCopy={() => void copy(s.command)} />
                  {s.note && <p className="text-xs text-muted-foreground pl-7">{s.note}</p>}
                </div>
              ))}
            </div>

            <p className="text-[11px] text-muted-foreground flex items-center gap-1">
              <ShieldAlert className="h-3 w-3" />
              Operazione distruttiva: eseguire solo con coordinamento del cluster Patroni.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
